import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import StructuredData from '../components/StructuredData'
import Button from '../components/site/Button'
import SectionHeading from '../components/site/SectionHeading'
import CtaBand from '../components/site/CtaBand'
import { identity, contact, services, career, publications, press, researchProjects } from '../data/profile'
import { personSchema, professionalServiceSchema } from '../lib/structuredData'

// Home (prototype Paper.dc.html minus the BRIEF deviations: no Work, no case study,
// "Request a 30-minute call"). Every fact below is read from src/data/profile.js.

const byEmployer = (employer) => career.find((c) => c.employer === employer)
// Years from the first analyst role to the end of the last finished role (2016 -> 2026).
const lastEnd = Math.max(...career.filter((c) => c.end).map((c) => Number(c.end.slice(0, 4))))
const analystYears = lastEnd - identity.analystSince
const [publication] = publications
const [firstProject, ...otherProjects] = researchProjects
const pressOutlets = [...new Set(press.map((p) => p.outlet))]

const FACTS = [
  { label: 'Published', value: `iiWAS ${publication.year}, ${publication.publisher} LNCS`, to: '/research' },
  { label: 'Practice', value: `~${analystYears} years as a business analyst`, to: '/about' },
  { label: 'Featured in', value: pressOutlets.join(', '), to: '/notes' },
  { label: 'Research projects', value: `${firstProject.name} and ${otherProjects.length} more`, to: '/research' },
]

// Offer copy: how Martin works, not past results (BRIEF Q14).
const SERVICE_SUMMARIES = {
  '/services/agent-systems':
    'Multi-agent workflows with defined roles, hand-offs and checks, predictable enough for customers.',
  '/services/research-pipelines':
    'Scheduled collection and synthesis on a topic you track, every claim linked to its source.',
  '/services/agentic-coding':
    'Claude Code or Cursor set up for your team with rules, review agents and git guards, plus training.',
}

const METHOD = [
  { step: 'Clarify', text: 'Five questions, one brief.' },
  { step: 'Plan', text: 'Every criterion has a check.' },
  { step: 'Build', text: 'Small slices, tests first.' },
  { step: 'Review', text: 'Independent, every time.' },
  { step: 'Measure', text: 'Logged, compared, improved.' },
]

const KEYWORDS = ['agents', 'spec-first', 'pipelines', 'DPP', 'evaluation']

const LEAD =
  'Agent workflows, research pipelines and agentic coding practice, each delivered with a written spec, an independent review and a number that shows whether it worked.'

const mono = 'font-mono text-xs uppercase tracking-wider text-ink/70'

export default function Home() {
  const taltech = byEmployer('TalTech')
  const kulg = byEmployer('KULG')

  return (
    <div className="grid-paper">
      <SEO
        title="Martin Tamm: AI systems that can be checked"
        description="Agent workflows, research pipelines and agentic coding, each delivered with a written spec, an independent review and a number that shows whether it worked."
        path="/"
      />
      <StructuredData data={personSchema()} />
      <StructuredData data={professionalServiceSchema()} />

      <section className="mx-auto max-w-[1180px] px-4 pb-14 pt-12 sm:px-6 sm:pt-18">
        <p className={`flex flex-wrap gap-x-6 gap-y-2 border-b border-ink pb-4 ${mono}`}>
          <span>Technical note</span>
          <span>{contact.location}</span>
          <span>Status: Open for projects</span>
        </p>

        <div className="mt-10 flex flex-wrap gap-12">
          <div className="min-w-0 flex-[999_1_560px]">
            <h1 className="font-serif text-[clamp(2.25rem,5.6vw,4.75rem)] font-bold leading-[1.04] tracking-tight text-ink">
              On building AI systems that can be{' '}
              <em className="font-medium text-signal">checked.</em>
            </h1>
            <p className="mt-7 max-w-[640px] text-lg text-ink/80 sm:text-xl">{LEAD}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button to={contact.primaryCta.path}>{contact.primaryCta.label}</Button>
              <Button to="/workspace" variant="secondary">
                Try the workspace
              </Button>
            </div>
          </div>

          <aside
            aria-label="Abstract"
            className="flex min-w-0 flex-[1_1_320px] flex-col gap-3.5 self-start border-2 border-ink bg-white p-6"
          >
            <p className={mono}>Abstract</p>
            <p className="font-serif text-lg leading-normal">
              Doctoral researcher at {taltech.employer} (Digital Product Passports) with about {analystYears} years as
              a business analyst, and head of product and AI at {kulg.employer}. I bring research method to commercial
              AI work.
            </p>
            <p className={`mt-1.5 ${mono}`}>Keywords</p>
            <ul className="flex flex-wrap gap-2 font-mono text-xs">
              {KEYWORDS.map((k) => (
                <li key={k} className="border border-ink px-2 py-0.5">
                  {k}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section aria-label="Facts" className="mx-auto max-w-[1180px] px-4 pb-10 sm:px-6">
        <ul className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-px border-2 border-ink bg-ink">
          {FACTS.map((f) => (
            <li key={f.label} className="bg-white">
              <Link to={f.to} className="block min-h-11 px-5 py-4 text-ink no-underline hover:bg-paper">
                <span className="block font-mono text-[11px] uppercase text-ink/70">{f.label}</span>
                <span className="font-semibold">{f.value}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="home-services" className="mx-auto max-w-[1180px] px-4 pb-18 pt-8 sm:px-6">
        <SectionHeading id="home-services" number={1}>
          Services
        </SectionHeading>
        <ul className="mt-5 border-b-2 border-ink">
          {services.map((s, i) => (
            <li key={s.path} className="border-b border-ink last:border-b-0">
              <Link
                to={s.path}
                className="group flex flex-wrap items-baseline gap-x-8 gap-y-3 px-3 py-7 text-ink no-underline hover:bg-white"
              >
                <span className="w-12 font-mono text-sm text-ink/70">1.{i + 1}</span>
                <span className="flex-[1_1_260px] font-serif text-[28px] font-bold leading-tight">{s.name}</span>
                <span className="flex-[2_1_380px] text-base text-ink/80">{SERVICE_SUMMARIES[s.path]}</span>
                <span aria-hidden="true" className="text-2xl group-hover:text-signal">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="home-method" className="mx-auto max-w-[1180px] px-4 pb-18 sm:px-6">
        <SectionHeading id="home-method" number={2}>
          Method
        </SectionHeading>
        <ol className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-px border-2 border-ink bg-ink">
          {METHOD.map((m, i) => (
            <li key={m.step} className="bg-white p-5">
              <div className="font-mono text-xs text-signal">FIG. {i + 1}</div>
              <div className="mt-1.5 font-semibold">{m.step}</div>
              <div className="text-[15px] text-ink/80">{m.text}</div>
            </li>
          ))}
        </ol>
      </section>

      <CtaBand title="Have a problem worth a spec? Write to me." label="Start a conversation" />
    </div>
  )
}
